'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import {
  ACTION_BUTTON_CLASSES,
  CARD_IMAGE_FRAME_CLASSES,
  CARD_TARGET_CLASSES,
} from '../bundle-cards/bundle-card';
import { ClassicPdpUpsellProduct } from '../classic-pdp-content';
import { ServiceNukiIdModal } from './service-nuki-id-modal/service-nuki-id-modal';

export interface UpsellServiceCardProps {
  product: ClassicPdpUpsellProduct;
}

/**
 * One linked service of the upsell grid (Smart Hosting, a digital product). It
 * looks like the product card, but a service has no colour or cylinder to
 * pick: it sells per Smart Lock ID, so the "+" collects the IDs in a modal on
 * the page. A click on the image or the text opens the page of the service.
 */
export function UpsellServiceCard({ product }: Readonly<UpsellServiceCardProps>) {
  const t = useTranslations('ClassicProductDetail');
  const { formatCurrency } = useCurrencyFormatter();
  const { localizedLink } = useLocaleLink();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <article
      data-testid="classic-pdp-upsell-service-card"
      className="relative flex h-full flex-col"
    >
      <div data-testid="classic-pdp-card-image-frame" className={CARD_IMAGE_FRAME_CLASSES}>
        <OptimizedImage
          image={product.image}
          width={300}
          height={300}
          sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 80vw"
          className="mx-auto h-40 w-full object-contain"
        />
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          aria-label={t('chooseBundle', { name: product.name })}
          aria-haspopup="dialog"
          className={ACTION_BUTTON_CLASSES}
        >
          +
        </button>
      </div>

      <h3 className="mt-3 font-bold text-neutral-100">
        <Link href={localizedLink(product.path)} className={CARD_TARGET_CLASSES}>
          {product.name}
        </Link>
      </h3>

      {/* Pinned to the bottom so the prices of neighbouring cards line up. */}
      <p className="mt-auto flex flex-wrap items-baseline gap-x-2 pt-1">
        {product.price !== undefined && (
          <span className="font-bold">
            {formatCurrency(product.price, { currency: product.currency })}
          </span>
        )}
      </p>

      <ServiceNukiIdModal
        product={product}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </article>
  );
}
